<template>
  <div id="welcome-step" class="form-container">
    <sui-form>
      <div is="sui-header">
        Clinical Information
        <!-- <sui-button basic primary inverted circular size="small" icon="question" /> -->
        <div class="how-to" @click.stop="$driver.start()">
          how to use it <sui-icon size="small" name="help" fitted />
        </div>
      </div>

      <div class="input-fields">
        <div id="gender-step">
          <div class="field">
            <label>Gender:</label>
            <div class="inline fields">
              <div class="field">
                <sui-checkbox
                  radio
                  v-model="data.gender"
                  name="gender"
                  value="male"
                />
                <i class="mars icon"></i><span>Male</span>
              </div>
              <div class="field">
                <sui-checkbox
                  radio
                  v-model="data.gender"
                  name="gender"
                  value="female"
                />
                <i class="venus icon"></i><span>Female</span>
              </div>
              <div class="field">
                <sui-checkbox
                  radio
                  v-model="data.gender"
                  name="gender"
                  value="NA"
                />
                <i class="genderless icon"></i><span>Unknown</span>
              </div>
            </div>
          </div>
        </div>

        <div class="field">
          <label>AML Type:</label>
          <div class="inline fields">
            <div class="field">
              <sui-checkbox
                radio
                v-model="data.secondary"
                label="Primary"
                name="secondary"
                value="1"
              />
            </div>
            <div class="field">
              <sui-checkbox
                radio
                v-model="data.secondary"
                label="Secondary"
                name="secondary"
                value="2"
              />
            </div>
            <div class="field">
              <sui-checkbox
                radio
                v-model="data.secondary"
                label="Other"
                name="secondary"
                value="3"
              />
            </div>
            <div class="field">
              <sui-checkbox
                radio
                v-model="data.secondary"
                label="N/A"
                name="secondary"
                value="NA"
              />
            </div>
          </div>
        </div>

        <div class="field">
          <label>Antecedent of Hematological Disorder: (ex. MDS)</label>
          <div class="inline fields">
            <div class="field">
              <sui-checkbox
                radio
                v-model="data.ahd"
                label="Yes"
                name="ahd"
                value="1"
              />
            </div>
            <div class="field">
              <sui-checkbox
                radio
                v-model="data.ahd"
                label="No"
                name="ahd"
                value="0"
              />
            </div>
            <div class="field">
              <sui-checkbox
                radio
                v-model="data.ahd"
                label="Unknown"
                name="ahd"
                value="NA"
              />
            </div>
          </div>
        </div>

        <div id="age-step">
          <sui-form-field :error="'age' in validationErrors">
            <label>Age at Diagnosis:</label>
            <div class="inline fields">
              <div class="eight wide field">
                <input
                  type="number"
                  v-model="data.age"
                  name="age"
                  placeholder="Enter Age"
                  @blur="validateFields"
                />
              </div>
              <div class="eight wide field">
                <span class="field-units"> <i>years</i> [0-120] </span>
              </div>
            </div>
          </sui-form-field>
        </div>

        <sui-form-field :error="'wbc' in validationErrors">
          <label>White Cell Count:</label>
          <div class="inline fields">
            <div class="eight wide field">
              <input
                v-model="data.wbc"
                type="number"
                name="wbc"
                step="0.1"
                placeholder="Enter Value"
                @blur="validateFields"
              />
            </div>
            <div class="eight wide field">
              <span class="field-units">
                <i>10<sup>9</sup>/l</i> [0.2-600]
              </span>
            </div>
          </div>
        </sui-form-field>

        <sui-form-field :error="'plt' in validationErrors">
          <label>Platelet Count:</label>
          <div class="inline fields">
            <div class="eight wide field">
              <input
                v-model="data.plt"
                type="number"
                name="plt"
                step="0.1"
                placeholder="Enter Value"
                @blur="validateFields"
              />
            </div>
            <div class="eight wide field">
              <span class="field-units">
                <i>10<sup>9</sup>/l</i> [2-3500]
              </span>
            </div>
          </div>
        </sui-form-field>

        <sui-form-field :error="'bm_blasts' in validationErrors">
          <label>Bone Marrow Blasts:</label>
          <div class="inline fields">
            <div class="eight wide field">
              <input
                v-model="data.bm_blasts"
                type="number"
                name="bm_blasts"
                step="0.1"
                placeholder="Enter Value"
                @blur="validateFields"
              />
            </div>
            <div class="eight wide field">
              <span class="field-units"> <i>%</i> [0-100] </span>
            </div>
          </div>
        </sui-form-field>

        <sui-form-field :error="'hb' in validationErrors">
          <label>Hemoglobin:</label>
          <div class="inline fields">
            <div class="eight wide field">
              <input
                v-model="data.hb"
                type="number"
                name="hb"
                step="0.1"
                placeholder="Enter Value"
                @blur="validateFields"
              />
            </div>
            <div class="eight wide field">
              <span class="field-units"> <i>g/dl</i> [2.5-18.0] </span>
            </div>
          </div>
        </sui-form-field>

        <div class="field">
          <label>Performance (ECOG):</label>
          <div class="rowed fields">
            <div
              v-for="[value, label] in Object.entries(
                fieldDefinitions['perf_status']['values']
              )"
              :key="value"
              class="field"
            >
              <sui-checkbox
                radio
                v-model="data.perf_status"
                name="perf_status"
                :value="value"
              >
                <span v-html="label" />
              </sui-checkbox>
            </div>
          </div>
        </div>

        <div id="molecular-step">
          <div is="sui-header">Known Driver Mutations</div>

          <div
            v-for="mutationType in Object.keys(mutations)"
            :key="mutationType"
            class="mutation-fields field"
          >
            <label>{{ mutationType }}:</label>
            <sui-dropdown
              fluid
              multiple
              search
              :options="mutations[mutationType]"
              :placeholder="`Add ${mutationType}`"
              selection
              v-model="selectedMutations[mutationType]"
            />

            <div
              v-if="mutationType == 'Genetic'"
              class="gene-panel-small-letter"
            >
              * Limited to a minimum set of 32 genes. (See
              <router-link to="gene-panel">Gene Panel</router-link>)
            </div>

            <div
              v-if="mutationType == 'CNA' && isComplexWithoutDeletions"
              class="gene-panel-small-letter"
            >
              * Complex (3 or more aneuploidies): If no deletion is selected,
              the model assumes all are additions.
            </div>
          </div>
        </div>

        <sui-message error :visible="hasErrors">
          <p
            v-for="({ label, min, max, units }, fieldName) in validationErrors"
            :key="fieldName"
          >
            <b
              >Invalid {{ label }}: {{ data[fieldName] }}
              <span v-html="units" />.</b
            >
            Please select a value between {{ min }} and {{ max }}
            <span v-html="units" />.
          </p>
        </sui-message>

        <div
          id="calculate-step"
          is="sui-button"
          :class="[
            'ui button calculate',
            elnLoading || thisPatientLoading ? 'loading' : ''
          ]"
          @click="calculate"
          :disabled="disabled"
        >
          Calculate Patient Outcome
        </div>
        <div is="sui-button" class="ui button reset" @click="resetForm">
          Reset
        </div>
      </div>
    </sui-form>

    <!-- Warning Modal -->
    <sui-modal class="warning-modal" v-model="openWarningModal">
      <sui-modal-header>⚠️ Low Confidence Model</sui-modal-header>
      <sui-modal-content tiny>
        <div>
          Some values are outside the 95% percentile band of the cohort range.
          This would impact in a lower confidence of the calculated patient risk
          model.
          <ul v-for="(value, index) in lowConfidence" :key="index">
            <li v-html="value"></li>
          </ul>
        </div>
      </sui-modal-content>
      <sui-modal-actions>
        <sui-button class="button dismiss" @click="dismissWarning">
          I prefer to not continue
        </sui-button>
        <sui-button class="button continue" @click="acceptWarning">
          I'm aware, I want to Continue
        </sui-button>
      </sui-modal-actions>
    </sui-modal>
  </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import fieldDefinitions from "@/utils/fields";
import mutations from "@/utils/mutations";
import { predict, newClassify, elnClassify, warmApi } from "@/utils/api";
import { mapGetters } from "vuex";

export default {
  name: "PatientInput",
  data() {
    return {
      mutations,
      fieldDefinitions,
      data: this.getDefaults(),
      validationErrors: {},
      fieldsToValidate: _.pickBy(fieldDefinitions, i => i.validation),
      selectedMutations: {},
      userIsOkWithLowConfidence: false,
      openWarningModal: false,
      lowConfidence: []
    };
  },
  mounted() {
    warmApi();
  },
  computed: {
    ...mapGetters(["thisPatientLoading", "elnLoading", "confidenceIntervals"]),
    hasErrors() {
      return !!Object.keys(this.validationErrors).length;
    },
    disabled() {
      return this.elnLoading || this.thisPatientLoading || this.hasErrors;
    },
    isComplexWithoutDeletions() {
      return (
        this.selectedMutations["CNA"] &&
        this.selectedMutations["CNA"].includes("complex") &&
        !this.selectedMutations["CNA"].filter(i => i.startsWith("del")).length
      );
    }
  },
  methods: {
    getDefaults() {
      return Object.fromEntries(
        Object.entries(fieldDefinitions).map(([fieldName, prop]) => [
          fieldName,
          prop.default
        ])
      );
    },
    resetForm() {
      this.data = this.getDefaults();
      this.validateFields();
    },
    dismissWarning() {
      this.openWarningModal = false;
    },
    acceptWarning() {
      this.userIsOkWithLowConfidence = true;
      this.openWarningModal = false;
      this.computeOutcome();
    },
    calculate() {
      if (!this.disabled) {
        this.validateCI();
      }
    },
    computeOutcome() {
      if (!this.lowConfidence.length || this.userIsOkWithLowConfidence) {
        this.$store.commit("setElnLoading", true);
        this.$store.commit("setNewLoading", true);
        this.$store.commit("setThisPatientLoading", true);

        document.querySelector("html").scrollIntoView({
          behavior: "smooth",
          block: "center"
        });

        // Clean Empty input fields
        const formData = Object.fromEntries(
          Object.entries(this.data).filter(
            ([, value]) => value && value !== "NA"
          )
        );

        // Clean Non-Valid Mutations from Selection Dropdown
        Object.entries(this.selectedMutations).forEach(
          ([mutationType, mutationList]) => {
            this.selectedMutations[mutationType] = mutationList.filter(
              mutation => {
                if (
                  this.mutations[mutationType]
                    .map(i => i.key)
                    .includes(mutation)
                ) {
                  formData[mutation] = 1;
                  return true;
                } else {
                  return false;
                }
              }
            );
          }
        );

        // If >=3 CNA, it is a complex karyotype
        if (
          this.selectedMutations["CNA"] &&
          this.selectedMutations["CNA"].length >= 3
        ) {
          formData["complex"] = 1;
        }

        // Update objects in store
        this.$store.commit("updatePatientData", formData);
        this.$store.commit("updatePatientMutations", this.selectedMutations);
        this.$store.commit("updatePredictError", false);

        // Create queries
        this.queryClassicators(formData);

        window.analytics.identify({ name: "anonymous" });
        window.analytics.track("computeOutcome", formData);
      } else if (this.lowConfidence.length) {
        this.openWarningModal = true;
      }
    },
    validateCI() {
      this.lowConfidence = [];
      const formData = Object.fromEntries(
        Object.entries(this.data).filter(([, value]) => value && value !== "NA")
      );
      Object.entries(this.confidenceIntervals).map(
        ([field, [lowCI, highCI]]) => {
          const units = fieldDefinitions[field].units || "";
          const label = fieldDefinitions[field].label;
          if (formData[field] < lowCI) {
            this.lowConfidence.push(`
            <p>
              97.5% of the patients of the cohort have a
              <span class="underlined">greater</span> <b>${label}</b> value.
            </p>
            <p>
              Patient has <b>${formData[field]} ${units}</b>.
              While 95% of the cohort are between ${Math.round(lowCI, 1)} and
              ${Math.round(highCI, 1)} ${units}.
            </p>
          `);
          } else if (formData[field] > highCI) {
            this.lowConfidence.push(`
            <p>
              97.5% of the patients of the cohort have a
              <span class="underlined">lower</span> <b>${label}</b> value.
            </p>
            <p>
              Patient has <b>${formData[field]} ${units}</b>.
              While 95% of the cohort are between ${Math.round(lowCI, 1)} and
              ${Math.round(highCI, 1)} ${units}.
            </p>
          `);
          }
        }
      );
      this.computeOutcome();
    },
    queryClassicators(formData) {
      elnClassify(formData)
        .then(response =>
          this.$store.commit("updateElnClassification", response)
        )
        .catch(() => {
          this.$store.commit("updateElnClassification", {});
          this.$store.commit("updatePredictError", true);
        })
        .finally(() => this.$store.commit("setElnLoading", false));

      newClassify(formData)
        .then(response =>
          this.$store.commit("updateNewClassification", response)
        )
        .catch(() => {
          this.$store.commit("updateNewClassification", {});
          this.$store.commit("updatePredictError", true);
        })
        .finally(() => this.$store.commit("setNewLoading", false));

      predict(formData)
        .then(response => this.$store.commit("updateThisPatient", response))
        .catch(() => {
          this.$store.commit("updateThisPatient", {});
          this.$store.commit("updatePredictError", true);
        })
        .finally(() => this.$store.commit("setThisPatientLoading", false));
    },
    validateFields() {
      this.validationErrors = {};
      Object.entries(this.fieldsToValidate).forEach(
        ([fieldName, fieldValues]) => {
          const { min, max } = fieldValues;
          const value = Number(this.data[fieldName]);
          if (value && (value < min || value > max)) {
            Vue.set(this.validationErrors, fieldName, fieldValues);
          }
        }
      );
    }
  }
};
</script>

<style lang="scss">
.form-container {
  border: 1px solid #edeffa;
  color: #5a6071;
  margin: 24px;
  user-select: none;

  form {
    background: white;
  }

  .how-to {
    color: #00113d75;
    cursor: pointer;
    display: inline;
    font-size: 13px;
    font-weight: 500;
    float: right;
  }

  .ui.header {
    background: #f6f9ff;
    border: 1px solid #edeffa;
    border-radius: 4px 4px 0px 0px;
    color: #00113d;
    font-family: DM Sans;
    font-size: 22px;
    padding: 12px;
  }
  .ui.form .field {
    padding-left: 12px;
    padding-right: 12px;
    font-family: DM Sans;

    input {
      border: 1px solid #dfe3ee;
      border-radius: 2px;
      color: #00113d;
      &:not(.search) {
        height: 40px;
      }
      &::placeholder {
        color: #c1c8d4;
        font-family: DM Sans;
      }
      &:focus {
        border-color: #2e68bd;
        border-radius: 2px;
      }
    }
    i.icon {
      color: #1e4781;
      font-size: 15px;
    }
    label {
      color: #00113d;
      font-family: DM Sans;
      font-size: 14px;
      margin: 0px;
    }
    label[for="perf_status"] {
      padding-left: 31px;
    }
    .inline-fields {
      margin: -0.5rem 0 0.5rem 0;
    }
    .inline.fields .field,
    .rowed .field {
      margin: 14px 0px;
      label {
        color: #5a6071;
        font-family: DM Sans;
        font-size: 13px;
        padding-left: 28px;
      }
      span {
        color: #5a6071;
        font-size: 13px;
      }
    }

    .rowed.fields {
      display: block;
    }

    &.mutation-fields {
      .dropdown {
        border: 1px solid #dfe3ee;
        border-radius: 2px;
        i {
          border: 0px;
        }
        &.icon {
          padding: 8px;
        }
      }
      input.search {
        width: 80px;
      }
      .ui.label {
        font-weight: 200;
        color: white;
        background: #2e68bd;
        border: 1px solid #dfe3ee;
        border-radius: 2px;
        i {
          color: white;
          font-size: 9px;
          margin-left: 10px;
          opacity: 0.7;
          vertical-align: 2px;
        }
      }
    }
  }
  .ui.radio.checkbox label:before {
    border: 1px solid #dfe3ee;
    width: 20px;
    height: 20px;
    top: -3px;
  }
  .ui.radio.checkbox input:checked ~ label:after {
    background: #2e68bd;
    width: 25px;
    height: 25px;
    top: -6px;
    left: -3px;
  }
  .ui.button {
    border: 1px solid transparent;
    padding: 14px 12px;
    border-radius: 2px;
    color: white;
    margin: 16px;
    &.calculate {
      background: #f2994a;
      width: 200px;
      &:hover {
        color: white;
        background: rgba(242, 153, 74, 0.8);
      }
      &.disabled {
        cursor: not-allowed;
        pointer-events: all !important;
      }
    }
    &.reset {
      background: white;
      color: #00113d;
      float: right;
      width: 80px;
      &:hover {
        border: 1px solid #dfe3ee;
      }
    }
    &.dismiss {
      background: #ddd;
      color: #00113d;
      float: right;
      width: 200px;
      &:hover {
        border: 1px solid #dfe3ee;
      }
    }
    &.continue {
      background: #f2994a;
      width: 250px;
      &:hover {
        color: white;
        background: rgba(242, 153, 74, 0.8);
      }
      &.disabled {
        cursor: not-allowed;
        pointer-events: all !important;
      }
    }
  }
  .ui.error.message {
    background-color: #ff252511;
    color: #ff2525;
    border: 1px solid #ff2525;
    border-radius: 0px;
    box-shadow: none;
  }
  .ui.form .field.error label {
    color: #ff2525;
    font-weight: 400;
  }
  .ui.form .field.error {
    input[type="number"],
    input[type="number"]:focus {
      color: #ff2525;
      background-color: #ff252511;
      border-color: #ff2525;
    }
  }
  #molecular-step label {
    padding-bottom: 8px;
    padding-left: 4px;
  }
  .gene-panel-small-letter {
    font-size: 12px;
    margin-top: 5px;
  }
  .warning-modal .ui.standard.modal {
    width: 640px;
  }
  .underlined {
    text-decoration: underline;
  }
}
</style>
